import moment from 'moment'
import store from '@/store'
import { IFBConvRequest } from '@/interfaces/fbconv'
import { sendFBConv } from '@/api/fbconv'

export function getBrowser (): string {
  const userAgent = navigator.userAgent
  let browser = 'unknown'
  // Detect browser name
  browser = (/ucbrowser/i).test(userAgent) ? 'UCBrowser' : browser
  browser = (/edg/i).test(userAgent) ? 'Edge' : browser
  browser = (/googlebot/i).test(userAgent) ? 'GoogleBot' : browser
  browser = (/chromium/i).test(userAgent) ? 'Chromium' : browser
  browser = (/firefox|fxios/i).test(userAgent) && !(/seamonkey/i).test(userAgent) ? 'Firefox' : browser
  browser = (/; msie|trident/i).test(userAgent) && !(/ucbrowser/i).test(userAgent) ? 'IE' : browser
  browser = (/chrome|crios/i).test(userAgent) && !(/opr|opera|chromium|edg|ucbrowser|googlebot/i).test(userAgent) ? 'Chrome' : browser
  browser = (/safari/i).test(userAgent) && !(/chromium|edg|ucbrowser|chrome|crios|opr|opera|fxios|firefox/i).test(userAgent) ? 'Safari' : browser
  browser = (/opr|opera/i).test(userAgent) ? 'Opera' : browser

  let version
  // detect browser version
  switch (browser) {
    case 'UCBrowser': return `${browser}/${browserVersion(userAgent, /(ucbrowser)\/([\d.]+)/i)}`
    case 'Edge': return `${browser}/${browserVersion(userAgent, /(edge|edga|edgios|edg)\/([\d.]+)/i)}`
    case 'GoogleBot': return `${browser}/${browserVersion(userAgent, /(googlebot)\/([\d.]+)/i)}`
    case 'Chromium': return `${browser}/${browserVersion(userAgent, /(chromium)\/([\d.]+)/i)}`
    case 'Firefox': return `${browser}/${browserVersion(userAgent, /(firefox|fxios)\/([\d.]+)/i)}`
    case 'Chrome': return `${browser}/${browserVersion(userAgent, /(chrome|crios)\/([\d.]+)/i)}`
    case 'Safari': return `${browser}/${browserVersion(userAgent, /(safari)\/([\d.]+)/i)}`
    case 'Opera': return `${browser}/${browserVersion(userAgent, /(opera|opr)\/([\d.]+)/i)}`
    case 'IE': version = browserVersion(userAgent, /(trident)\/([\d.]+)/i)
      // IE version is mapped using trident version
      // IE/8.0 = Trident/4.0, IE/9.0 = Trident/5.0
      return version ? `${browser}/${parseFloat(version) + 4.0}` : `${browser}/7.0`
    default: return userAgent
  }
}

const browserVersion = (userAgent: string, regex: RegExp) => {
  const result = userAgent.match(regex)
  if (result !== null) { return result[2] } else return null
}

export function randHex (len: number): string {
  const maxlen = 8
  const min = Math.pow(16, Math.min(len, maxlen) - 1)
  const max = Math.pow(16, Math.min(len, maxlen)) - 1
  const n = Math.floor(Math.random() * (max - min + 1)) + min
  let r = n.toString(16)
  while (r.length < len) {
    r = r + randHex(len - maxlen)
  }
  return r
}

/*
get hostId from local storage and set it
*/
export function getAndSetHostIdFromLocalStorage (): string {
  const hostId = localStorage.getItem('eco/hostId')
  if (hostId) { return hostId }
  const generatedHostId = randHex(8)
  setHostId(generatedHostId)
  return generatedHostId
}

function setHostId (hostId: string): void {
  localStorage.setItem('eco/hostId', hostId)
}

/*
get hostId from URL which passed by App webview
*/
export function getAndSetHostIdFromUrl (url: string): string | null {
  // Remove hash, due to parsing error
  const urlObj = new URL(url.replace('#/', ''))
  const hostId = urlObj.searchParams.get('hostId')
  if (hostId) setHostId(hostId)
  return hostId
}

export function getTokenFromUrl (url: string) : string | null {
  // Remove hash, due to parsing error
  const urlObj = new URL(url.replace('#/', ''))
  const token = urlObj.searchParams.get('token')
  return token
}

export function getCampaignFromUrl (url: string) : string | null {
  // Remove hash, due to parsing error
  const urlObj = new URL(url.replace('#/', ''))
  const campaign = urlObj.searchParams.get('campaign')
  return campaign
}

export function getFacebookParamsFromUrl (url: string) : { [key: string]: string | null } {
  // Remove hash, due to parsing error
  const urlObj = new URL(url.replace('login?#', 'login?'))
  const state = urlObj.searchParams.get('state')
  const code = urlObj.searchParams.get('code')
  return { state, code }
}

export function setCampaign (value : string) : void{
  localStorage.setItem('FBAdString', value)
}

export function generateImagePath (platformPrefix: string): string {
  const formattedDateWithoutYear = moment().format('MMDDHHmmssSSS')
  const c = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  const random = [...Array(20)].map(_ => c[~~(Math.random() * c.length)]).join('')
  const hostId = getAndSetHostIdFromLocalStorage()
  return `${platformPrefix}${hostId}${formattedDateWithoutYear}${random}`
}

export function fbTrack (event: string, param: any = null): void {
  const eventId = fbEventId()
  let fconvParams = {
    event_name: event,
    event_id: eventId,
    url: window.location.href
  }

  if (param === null) {
    window.fbq('track', event, {}, { eventID: eventId })
  } else {
    window.fbq('track', event, param, { eventID: eventId })
    Object.keys(param).forEach((key) => {
      fconvParams = { ...fconvParams, ...{ [key]: param[key] } }
    })
  }

  const umail = localStorage.getItem('userEmail')
  if (umail != null && umail !== '') {
    fconvParams = { ...fconvParams, ...{ email: umail } }
  }

  const fName = localStorage.getItem('userName')
  if (fName != null && fName !== '') {
    fconvParams = { ...fconvParams, ...{ first_name: fName } }
  }

  const phoneNumber = localStorage.getItem('userPhone')
  if (phoneNumber != null && phoneNumber !== '') {
    fconvParams = { ...fconvParams, ...{ phone: phoneNumber } }
  }

  if (!store.getters.bypassConv) {
    sendFBConv(fconvParams as IFBConvRequest)
  }
}

function fbEventId (): string {
  const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const charLength = chars.length
  let randomString = ''
  for (let i = 0; i < 8; i += 1) {
    const rand = Math.floor(Math.random() * charLength)
    randomString += chars[rand]
  }
  return randomString
}

export function isChinese (text: string) {
  // const lengthLimit = [min || 1, max || ''].join()
  const regex = new RegExp('[\u4E00-\u9FA5]', 'g')
  return typeof text === 'string' && text.match(regex)
}

export function isEnglish (text: string) {
  // const lengthLimit = [min || 1, max || ''].join()
  const regex = new RegExp('[a-zA-Z]', 'g')
  return typeof text === 'string' && text.match(regex)
}

export function isEmoji (text: string) {
  return typeof text === 'string' && text.match(/(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|[\ud83c[\ude32-\ude3a]|[\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g)
}

export function gernerateOrderId () {
  let randomStr = ''
  for (let i = 0; i < 5; i++) {
    randomStr += Math.floor(Math.random() * 10)
  }
  const formattedDate = moment().format('YYMMDDHHmmssSSS')
  const orederId = `${formattedDate}${randomStr}`
  return orederId.length === 20 ? orederId : undefined
}

export function validateMessage (target: string | number | null | undefined) {
  const reg = new RegExp(/[^\u4E00-\u9FFF\u3105-\u3126\u02ca\u02c7\u02cb\u02d9a-zA-Z \u3000\n~!@#$%^'&’‘*+()\d-_=?<>\uff0c\u3002\u3001\uff1a\uff01\uff1f\uff1b\uff08\uff09\u300c\u300d\u3010\u3011\uff20\uff03\uff04\uff05\uff3e\uff06\uff0a\u2212\uff0b\uff1d\uff21-\uff3a\uff41-\uff5a]/, 'g')
  if (typeof (target) === 'string' && (reg.test(target))) {
    return target.match(reg) || ''
  }
  return ''
}

export function checkNoMeshInProduct (id: number, title: string): boolean {
  return (id === 120 && title.includes('不含無框畫'))
}

export function checkNoMeshById (id: number): boolean {
  return (id === 120)
}
